/**
 * scheduler-rpc-client
 *
 * scheduler.v1.* RPCs against the "scheduler" logical mailbox.
 *
 * Extracted from the qubes schedules view, which owned the only copy, once a SECOND caller appeared:
 * the monitor detail page re-runs a monitor's check after an operator resets a breaker baseline
 * (FEATURE-20260805-monitor-acknowledge-control). Two hand-rolled copies of a raw RPC call drift in
 * exactly the place that matters — the error unwrapping, where a silently-swallowed ErrorResponse reads
 * to the operator as a button that worked.
 */

import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox } from "../hermesclient/types";
import { adminRpc } from "./admin-rpc-client";

/** What RunJobNow hands back on success. */
export interface RunJobNowResult {
  /**
   * The jobevent the run created — the handle to follow it to its outcome. Absent when talking to a
   * scheduler older than FEATURE-20260913-runjobnow-returns-its-job-event.
   */
  jobEventUid?: string;
}

// parseRunJobNowResponse reads the scheduler's JSON answer. The gateway marshals with protojson's
// default lowerCamel names (jobEventUid); the snake form is accepted too so a server switching to
// proto names does not silently drop the uid. A `success: false` answer is the scheduler DECLINING
// (not found, disabled, not authorized) and throws with its message.
export function parseRunJobNowResponse(data: Record<string, unknown> | undefined): RunJobNowResult {
  if (!data) return {};
  if (!data["success"]) {
    throw new Error((data["message"] as string) || "RunJobNow failed");
  }
  const uid = (data["jobEventUid"] ?? data["job_event_uid"]) as string | undefined;
  return uid ? { jobEventUid: uid } : {};
}

// runJobNow triggers an immediate run of one schedule. `reason` is recorded for audit — always say who
// asked and why, because a manual run appears in the schedule's history indistinguishable from a timed
// one otherwise.
export async function runJobNow(scheduleId: string, reason: string): Promise<RunJobNowResult> {
  const client = GlobalClient.get();
  const schedulerMailbox = await nameMappingService.resolve(
    asLogicalMailbox("scheduler"),
  );

  const request = { schedule_id: scheduleId, reason };
  const requestBytes = new TextEncoder().encode(JSON.stringify(request));

  const rpcResponse = await client.rawRpcCall({
    to: schedulerMailbox,
    endPoint: "scheduler.v1.RunJobNow",
    body: requestBytes,
    contentType: ContentType.Json,
  });

  const frameType = rpcResponse.header?.rpcHeader?.frameType;
  if (frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
    const errorMessage =
      errorInfo?.message ||
      errorInfo?.errorNode?.frame?.message ||
      "RPC call failed";
    throw new Error(errorMessage);
  }

  if (rpcResponse.data && rpcResponse.data.length > 0) {
    const responseText = new TextDecoder().decode(rpcResponse.data);
    return parseRunJobNowResponse(JSON.parse(responseText));
  }
  return {};
}

// runJob runs a job once with no schedule (scheduler.v1.RunJob): on the job's own worker, or on
// `workerUid`. `extraData` goes on the jobevent for the process that runs; a checkpoint job reads
// { checkpoint: { main } } or { checkpoint: { resume } } from it.
export async function runJob(
  jobUid: string,
  opts: { workerUid?: string; extraData?: Record<string, unknown>; reason?: string } = {},
): Promise<RunJobNowResult> {
  const client = GlobalClient.get();
  const schedulerMailbox = await nameMappingService.resolve(asLogicalMailbox("scheduler"));
  const request = {
    job_uid: jobUid,
    worker_uid: opts.workerUid ?? "",
    extra_data_json: opts.extraData ? JSON.stringify(opts.extraData) : "",
    reason: opts.reason ?? "",
  };
  const rpcResponse = await client.rawRpcCall({
    to: schedulerMailbox,
    endPoint: "scheduler.v1.RunJob",
    body: new TextEncoder().encode(JSON.stringify(request)),
    contentType: ContentType.Json,
  });
  if (rpcResponse.header?.rpcHeader?.frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
    throw new Error(errorInfo?.message || errorInfo?.errorNode?.frame?.message || "RPC call failed");
  }
  if (rpcResponse.data && rpcResponse.data.length > 0) {
    return parseRunJobNowResponse(JSON.parse(new TextDecoder().decode(rpcResponse.data)));
  }
  return {};
}

/**
 * Where a triggered run is, as the Run Now dialog shows it. `submitted` until the runner links a
 * processrun to the jobevent, then the processrun's own columns decide: the jobevent's status is
 * only a copy of them, written when ProcessCompleted arrives.
 */
export type RunState =
  | { phase: "submitted" }
  | { phase: "running"; processRunUid: string }
  | { phase: "completed"; processRunUid: string; outcome: "success" | "failed" | "awol"; exitCode: number }
  | { phase: "not-started"; message: string };

const TERMINAL_STATUSES = new Set(["success", "failed", "abandoned"]);

/** How often a followed run is re-read, and how long it is followed before giving up quietly. */
export const FOLLOW_POLL_MS = 2_000;
export const FOLLOW_MAX_MS = 30 * 60_000;

type Row = Record<string, unknown>;
type Sleep = (ms: number) => Promise<void>;
const realSleep: Sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** The state a jobevent row alone can tell: still waiting for a processrun, linked to one, or over without one. */
export function stateFromJobEvent(row: Row | undefined): RunState {
  const processRunUid = (row?.["process_run_uid"] as string) || undefined;
  if (processRunUid) return { phase: "running", processRunUid };
  const status = String(row?.["status"] ?? "");
  if (TERMINAL_STATUSES.has(status)) {
    return { phase: "not-started", message: (row?.["error_message"] as string) || `the job event ended ${status} with no process run` };
  }
  return { phase: "submitted" };
}

/** The state a processrun row tells. Reads the firewall's completedAt / processCompleteKind / exitCode. */
export function stateFromProcessRun(processRunUid: string, row: Row | undefined): RunState {
  if (!row || !(row["completedAt"] ?? row["completedat"])) return { phase: "running", processRunUid };
  const exitCode = Number(row["exitCode"] ?? row["exitcode"] ?? 0);
  const kind = row["processCompleteKind"] ?? row["processcompletekind"];
  const outcome = kind === "awol" ? "awol" : exitCode === 0 ? "success" : "failed";
  return { phase: "completed", processRunUid, outcome, exitCode };
}

/** One line for a state. A completed run says HOW: success (exit code 0), failure (exit code N), failure (awol). */
export function runStateLabel(state: RunState): string {
  switch (state.phase) {
    case "submitted":
      return "Submitted";
    case "running":
      return "Running";
    case "not-started":
      return `Did not start: ${state.message}`;
    case "completed":
      if (state.outcome === "awol") return "Completed: failure (awol)";
      return state.outcome === "success" ? "Completed: success (exit code 0)" : `Completed: failure (exit code ${state.exitCode})`;
  }
}

/** One line of the Run Now checklist. `waiting` is the step the run has not reached yet and is being watched for. */
export type RunStep = { label: string; status: "done" | "waiting" | "pending" | "failed" };

/** The run as a checklist: submitted, running, completed. Every step that has happened stays on the list. */
export function runSteps(state: RunState): RunStep[] {
  const submitted: RunStep = { label: "Submitted", status: "done" };
  switch (state.phase) {
    case "submitted":
      return [submitted, { label: "Running", status: "waiting" }, { label: "Completed", status: "pending" }];
    case "not-started":
      return [submitted, { label: runStateLabel(state), status: "failed" }, { label: "Completed", status: "pending" }];
    case "running":
      return [submitted, { label: "Running", status: "done" }, { label: "Completed", status: "waiting" }];
    case "completed":
      return [
        submitted,
        { label: "Running", status: "done" },
        { label: runStateLabel(state), status: state.outcome === "success" ? "done" : "failed" },
      ];
  }
}

// logRunFor answers whose log a run's output is in. A turn of a checkpoint program prints to its
// CHECKPOINT PROCESS's console, the overarching run above it (category checkpoint-process), not to its
// own; every other run's log is its own. Walks up at most a few parents.
export async function logRunFor(
  processRunUid: string,
  query: (sql: string) => Promise<Row[]> = async (sql) => (await adminRpc.query(sql)).rows,
): Promise<string> {
  let uid = processRunUid;
  try {
    for (let hop = 0; hop < 6 && /^[A-Za-z0-9]+$/.test(uid); hop++) {
      const row = (await query(`SELECT category AS category, parentProcessRunUid AS parent FROM processrun WHERE uid = '${uid}'`))[0];
      if (!row) break;
      if (row["category"] === "checkpoint-process") return uid;
      uid = String(row["parent"] ?? "");
    }
  } catch (e) {
    console.warn(`logRunFor ${processRunUid}: lookup failed, using the run's own log`, e);
  }
  return processRunUid;
}

// followRun follows ONE triggered run from its jobevent to the end of its processrun, calling onState
// each time the state changes, and resolves with the last state seen. It reads one row by primary key
// per poll: the jobevent until a processrun is linked, that processrun after. Gives up after maxMs.
export async function followRun(
  jobEventUid: string,
  onState: (state: RunState) => void,
  query: (sql: string) => Promise<Row[]> = async (sql) => (await adminRpc.query(sql)).rows,
  opts: { pollMs?: number; maxMs?: number; sleep?: Sleep; now?: () => number } = {},
): Promise<RunState> {
  const pollMs = opts.pollMs ?? FOLLOW_POLL_MS;
  const maxMs = opts.maxMs ?? FOLLOW_MAX_MS;
  const sleep = opts.sleep ?? realSleep;
  const now = opts.now ?? Date.now;
  let state: RunState = { phase: "submitted" };
  onState(state);
  if (!/^[A-Za-z0-9]+$/.test(jobEventUid)) return state; // uids are alphanumeric; never inline anything else
  const deadline = now() + maxMs;
  while (now() < deadline) {
    let next: RunState = state;
    try {
      if (state.phase === "submitted") {
        next = stateFromJobEvent((await query(`SELECT status, process_run_uid, error_message FROM jobevent WHERE uid = '${jobEventUid}'`))[0]);
      }
      if (next.phase === "running" && /^[A-Za-z0-9]+$/.test(next.processRunUid)) {
        const uid = next.processRunUid;
        next = stateFromProcessRun(uid, (await query(`SELECT completedAt, processCompleteKind, exitCode FROM processrun WHERE uid = '${uid}'`))[0]);
      }
    } catch (e) {
      console.warn(`followRun ${jobEventUid}: read failed, will retry`, e);
    }
    if (runStateLabel(next) !== runStateLabel(state)) {
      state = next;
      onState(state);
    }
    if (state.phase === "completed" || state.phase === "not-started") return state;
    await sleep(pollMs);
  }
  return state;
}
